import { useEffect, useState } from 'react';
import { Modal } from 'src/ui/Modal';
import { Button } from 'src/ui/Button';
import { Field } from 'src/ui/Field';
import { Select } from 'src/ui/Select';
import { Input } from 'src/ui/Input';
import { Alert } from 'src/ui/Alert';
import { SimulationGoal, StartSimulationDto } from 'src/store/simulationApi';

const MAX_TURNS = 80;

const GOALS: { value: SimulationGoal; label: string; turns: number }[] = [
  { value: 'first_card', label: 'First card shown', turns: 20 },
  { value: 'half', label: 'Profile reaches 50%', turns: 80 },
  { value: 'none', label: 'No goal (run to the cap)', turns: 20 },
];

interface SimulateModalProps {
  open: boolean;
  onClose: () => void;
  /** Name shown in the subtitle. */
  sourceName: string;
  sourceUserId: string;
  /** Default reply language, from the user's own setting. */
  defaultLang: 'en' | 'cn';
  previewCount: number;
  promptOverrides: Record<string, number>;
  loading?: boolean;
  onStart: (dto: StartSimulationDto) => void;
}

/** Settings for a new simulation run of the impersonated user. */
const SimulateModal = ({
  open,
  onClose,
  sourceName,
  sourceUserId,
  defaultLang,
  previewCount,
  promptOverrides,
  loading,
  onStart,
}: SimulateModalProps) => {
  const [goal, setGoal] = useState<SimulationGoal>('first_card');
  const [maxTurns, setMaxTurns] = useState(20);
  const [lang, setLang] = useState<'en' | 'cn'>(defaultLang);

  useEffect(() => {
    if (open) setLang(defaultLang);
  }, [open, defaultLang]);

  const turnsValid =
    Number.isInteger(maxTurns) && maxTurns >= 1 && maxTurns <= MAX_TURNS;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Simulate an interview"
      subtitle={`An AI plays ${sourceName} against Hong on a test copy.`}
      width={460}
      footer={
        <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon="play"
            loading={loading}
            disabled={!turnsValid}
            onClick={() =>
              onStart({
                sourceUserId,
                goal,
                maxTurns,
                lang,
                ...(previewCount ? { promptOverrides } : {}),
              })
            }
          >
            Start
          </Button>
        </div>
      }
    >
      <div className="col gap-3">
        <Alert color="blue" title="The real user is never touched">
          The run uses a TEST copy with the same basics and photos and a blank
          interview. Its answers come from this user's real profile.
        </Alert>
        <Field label="Stop when">
          <Select
            value={goal}
            onChange={(e) => {
              const next = GOALS.find((g) => g.value === e.target.value);
              if (!next) return;
              setGoal(next.value);
              setMaxTurns(next.turns);
            }}
          >
            {GOALS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Turn cap"
          hint={`User messages, 1–${MAX_TURNS}. Real users see the first card after about 4 and reach 50% after about 60.`}
        >
          <Input
            type="number"
            min={1}
            max={MAX_TURNS}
            value={maxTurns}
            onChange={(e) => setMaxTurns(Number(e.target.value))}
          />
        </Field>
        <Field label="Reply language">
          <Select
            value={lang}
            onChange={(e) => setLang(e.target.value as 'en' | 'cn')}
          >
            <option value="cn">简体中文</option>
            <option value="en">English</option>
          </Select>
        </Field>
        {previewCount > 0 && (
          <Alert color="amber" title={`${previewCount} previewed prompt(s)`}>
            The run uses the prompt versions you are previewing.
          </Alert>
        )}
      </div>
    </Modal>
  );
};

export default SimulateModal;
